import React, { useState, useEffect, useRef} from "react";
import { Row, Container, Col } from "reactstrap";
import {
  ErrorBoundary,
  SearchProvider,
  Paging,
  WithSearch,
} from "@elastic/react-search-ui";
import { Layout } from "@elastic/react-search-ui-views";
import { useParams } from "react-router-dom";

import "@elastic/react-search-ui-views/lib/styles/styles.css";
import { queryProcessor } from "core/utils/processor";
import { Sidecontent } from "components/Sidecontent";
import { Searchbox } from "components/Searchbox";
import { Bodycontent } from "components/Bodycontent";
import LoadingOverlay from "react-loading-overlay";
import { Appfooter } from "components/Appfooter";
import "./Homesearch.scss";
import connector from "services/search.service";
import { GetSavedSearchUUID,GetTranslatedResult_Service } from 'services/savedSearch.service';
import { useNavigate } from 'react-router-dom';
import CountryWithShortName,{ LanguageWithShortName } from "services/countrywithshortname";
import { getSearchResults, appSearch } from 'services/search.service';

export const Homesearch = () => {
  const navigate = useNavigate();
  const { searchTerm = "" } = useParams();
  const [config, setConfig] = useState({});
  const [isload, setisload] = useState(false);
  const [searchValue, setSearchValue] = useState(searchTerm);
  const [effectiveDate,setEffectiveDate]=useState("1950-10-05T14:48:00.000Z");
  const [showFilter,setshowFilter]=useState(false);
  const [searchTranslatedValue, setsearchTranslatedValue] = useState("");
  const [searchTranslatedWord, setsearchTranslatedWord] = useState("");

  // Org facets selection
  const [selectedFacets, setSelectedFacets] = useState({
    org_level_1: null,
    org_level_2: null,
    org_level_3: null,
    org_level_4: null,
    org_level_5: null,
    org_level_6: null,
    org_level_7: null,
    org_level_8: null,
    org_level_9: null,
    org_level_10: null,
  });

  // Library facets selection
  const [selectedLibrary, setSelectedLibrary] = useState({
    source_name_ims: null,
    source_sublevel: null
  });

  // Regulation facets selection (document_type → document_subtype)
  const [selectedRegulation, setSelectedRegulation] = useState({
    document_type: null,
    document_subtype: null,
  });

  const [expanded, setExpanded] = useState(new Set());

  // Separate caches for org, library, and regulation facets
  const [subCache, setSubCache] = useState({});
  const [sourceSubCache, setSourceSubCache] = useState({});
  const [regulationSubCache, setRegulationSubCache] = useState({});

  const facetsCopyRef = useRef(null);
  let facetsCopy;

  // Helper: transform selections to filter payload
  const transformData = (data) => {
    const result = [];
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const values = data[key];
        if (values && values.length > 0) {
          result.push({
            field: key,
            values: values
          });
        }
      }
    }
    return result;
  };

  // Combine org + library + regulation selections
  const buildCombinedFilters = (org, lib, reg = {}) => {
    const merged = { ...org, ...lib, ...reg };
    return transformData(merged);
  };

  // Org sublevels fetch
  const fetchData = async (body, level) => {
    try {
      const resp = await appSearch(body);
      const sublevels = resp.data?.facets[`org_level_${level + 1}`]?.[0]?.data || [];
      return sublevels;
    } catch (error) {
      console.error('Error fetching data:', error);
      return [];
    }
  };

  // Library sublevels fetch
  const fetchSourceData = async (body) => {
    try {
      const resp = await appSearch(body);
      const sublevels = resp.data?.facets[`source_sublevel`]?.[0]?.data || [];
      return sublevels;
    } catch (error) {
      console.error('Error fetching library data:', error);
      return [];
    }
  };

  // Regulation sublevels fetch
  const fetchRegulationData = async (body, nextFacetKey) => {
    try {
      const resp = await appSearch(body);
      const sublevels = resp.data?.facets[nextFacetKey]?.[0]?.data || [];
      return sublevels;
    } catch (error) {
      console.error('Error fetching regulation data:', error);
      return [];
    }
  };

  // Org expand/collapse
  const handleExpand = async (topValue, level, userQuery) => {
    if (expanded.has(topValue)) {
      const next = new Set(expanded);
      next.delete(topValue);
      setExpanded(next);
      setSubCache(prev => {
        const newCache = { ...prev };
        delete newCache[topValue];
        return newCache;
      });
      return;
    }

    if (!subCache[topValue]) {
      const body = {
        query: userQuery || "",
        filters: { [`org_level_${level}`]: [topValue] },
        facets: {
          [`org_level_${level + 1}`]: { type: "value", size: 200, sort: { value: "asc" } }
        },
        page: { size: 0 }
      };

      const fetchedSublevels = await fetchData(body, level);
      setSubCache(prev => ({
        ...prev,
        [topValue]: fetchedSublevels
      }));
    }

    const next = new Set(expanded);
    next.add(topValue);
    setExpanded(next);
  };

  // Library expand/collapse: Level 1 = source_name_ims (expandable), Level 2 = source_sublevel (no expand)
  const handleExpandLibrary = async (topValue, level, userQuery) => {
    // Only allow expand from source_name_ims (level 1)
    if (level !== 1) return;

    if (expanded.has(topValue)) {
      const next = new Set(expanded);
      next.delete(topValue);
      setExpanded(next);
      setSourceSubCache(prev => {
        const newCache = { ...prev };
        delete newCache[topValue];
        return newCache;
      });
      return;
    }

    if (!sourceSubCache[topValue]) {
      const body = {
        query: userQuery || "",
        filters: { source_name_ims: [topValue] },
        facets: {
          source_sublevel: { type: "value", size: 200, sort: { value: "asc" } }
        },
        page: { size: 0 }
      };

      const fetchedSublevels = await fetchSourceData(body);
      setSourceSubCache(prev => ({
        ...prev,
        [topValue]: fetchedSublevels
      }));
    }

    const next = new Set(expanded);
    next.add(topValue);
    setExpanded(next);
  };

  // Regulation expand/collapse: Level 1 = document_type (expandable), Level 2 = document_subtype (no expand)
  const handleExpandRegulation = async (topValue, currentLevel, userQuery) => {
    // Only allow expand from document_type (level 1)
    if (currentLevel !== 1) return;

    if (expanded.has(topValue)) {
      const next = new Set(expanded);
      next.delete(topValue);
      setExpanded(next);
      setRegulationSubCache(prev => {
        const newCache = { ...prev };
        delete newCache[topValue];
        return newCache;
      });
      return;
    }

    const nextFacetKey = 'document_subtype';
    const filterKey = 'document_type';

    if (!regulationSubCache[topValue]) {
      const body = {
        query: userQuery || "",
        filters: { [filterKey]: [topValue] },
        facets: {
          [nextFacetKey]: { type: "value", size: 200, sort: { value: "asc" } }
        },
        page: { size: 0 }
      };

      const fetchedSublevels = await fetchRegulationData(body, nextFacetKey);
      setRegulationSubCache(prev => ({
        ...prev,
        [topValue]: fetchedSublevels
      }));
    }

    const next = new Set(expanded);
    next.add(topValue);
    setExpanded(next);
  };

  // Org checkbox toggle
  const handleFacetClick = (level, value) => {
    const facetKey = `org_level_${level}`;
    const newSelectedFacets = { ...selectedFacets };

    if (!newSelectedFacets[facetKey]) {
      newSelectedFacets[facetKey] = [];
    }

    if (newSelectedFacets[facetKey].includes(value)) {
      newSelectedFacets[facetKey] = newSelectedFacets[facetKey].filter(item => item !== value);
    } else {
      newSelectedFacets[facetKey].push(value);
    }

    setSelectedFacets(newSelectedFacets);
    const combined = buildCombinedFilters(newSelectedFacets, selectedLibrary, selectedRegulation);
    performSearch(combined);
  };

  // Library checkbox toggle
  const handleLibraryFacetClick = (currentLevel, value) => {
    const facetKey = currentLevel === 1 ? 'source_name_ims' : 'source_sublevel';
    const newSelectedLibrary = { ...selectedLibrary };

    if (!newSelectedLibrary[facetKey]) {
      newSelectedLibrary[facetKey] = [];
    }

    if (newSelectedLibrary[facetKey].includes(value)) {
      newSelectedLibrary[facetKey] = newSelectedLibrary[facetKey].filter(item => item !== value);
    } else {
      newSelectedLibrary[facetKey].push(value);
    }

    setSelectedLibrary(newSelectedLibrary);
    const combined = buildCombinedFilters(selectedFacets, newSelectedLibrary, selectedRegulation);
    performSearch(combined);
  };

  // Regulation checkbox toggle: Level 1 = document_type, Level 2 = document_subtype
  const handleRegulationFacetClick = (currentLevel, value) => {
    const facetKey = currentLevel === 1 ? 'document_type' : 'document_subtype';
    const newSelectedRegulation = { ...selectedRegulation };

    if (!newSelectedRegulation[facetKey]) {
      newSelectedRegulation[facetKey] = [];
    }

    if (newSelectedRegulation[facetKey].includes(value)) {
      newSelectedRegulation[facetKey] = newSelectedRegulation[facetKey].filter(item => item !== value);
    } else {
      newSelectedRegulation[facetKey].push(value);
    }

    setSelectedRegulation(newSelectedRegulation);
    const combined = buildCombinedFilters(selectedFacets, selectedLibrary, newSelectedRegulation);
    performSearch(combined);
  };

  // Trigger search with filter payload
  const performSearch = (searchQuery) => {
    setTimeout(()=>{
      getSearch("",false,true,searchQuery);
    },100);
  };

  // Render org facets
  const renderFacets = (level, facets) => {
    const facetKey = `org_level_${level}`;

    if (!facets || !facets[facetKey] || facets[facetKey].length === 0) {
      return <div>No facets available for this level.</div>;
    }

    if (level === 1 && !facetsCopyRef.current) {
      facetsCopyRef.current = structuredClone(facets);
    }
    facetsCopy = facetsCopyRef.current;

    let facetData;
    if (level === 1) {
      facetData = facetsCopy[facetKey][0].data;
    } else {
      facetData = facets[facetKey][0].data;
    }

    const renderFacetItem = (bucket, currentLevel) => {
      if (bucket.value === 'Not Available') return null;

      const currentFacetKey = `org_level_${currentLevel}`;
      const hasSubLevels = subCache[bucket.value] && subCache[bucket.value].length > 0;

      return (
        <div key={bucket.value} className={`facet-item`} style={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {currentLevel !== 10 && (
              <span onClick={() => handleExpand(bucket.value, currentLevel, bucket.value)} style={{ cursor: 'pointer', marginRight: '8px' }}>
                {expanded.has(bucket.value) ? <i className="fa fa-caret-down" aria-hidden="true"></i> : <i className="fa fa-caret-right" aria-hidden="true"></i>}
              </span>
            )}
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={selectedFacets[currentFacetKey]?.includes(bucket.value)}
                onChange={() => handleFacetClick(currentLevel, bucket.value)}
                style={{ marginRight: '8px' }}
              />
              {bucket.value} <span className="facet-count">({bucket.count})</span>
            </label>
          </div>

          {expanded.has(bucket.value) && (
            <div style={{ marginLeft: '30px', marginTop: '4px', display: 'flex', flexDirection: 'column' }}>
              {hasSubLevels ? (
                subCache[bucket.value].map((subBucket) => renderFacetItem(subBucket, currentLevel + 1))
              ) : (
                <div style={{ marginLeft: '20px', color: 'gray' }}>No more sublevels available.</div>
              )}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="facet-container">
        {facetData.map((bucket) => renderFacetItem(bucket, level))}
      </div>
    );
  };

  // Render library facets (source_name_ims → source_sublevel)
  const renderLibrary = (level, facets) => {
    const facetKey = `source_name_ims`;

    if (!facets || !facets[facetKey] || facets[facetKey].length === 0) {
      return <div>No sources available for this level.</div>;
    }

    if (level === 1 && !facetsCopyRef.current) {
      facetsCopyRef.current = structuredClone(facets);
    }
    facetsCopy = facetsCopyRef.current;

    let facetData;
    if (level === 1) {
      facetData = facetsCopy[facetKey][0].data;
    } else {
      facetData = facets[facetKey][0].data;
    }

    const renderFacetItem = (bucket, currentLevel) => {
      if (bucket.value === 'Not Available') return null;

      const currentFacetKey = currentLevel === 1 ? 'source_name_ims' : 'source_sublevel';
      const hasSubLevels = sourceSubCache[bucket.value] && sourceSubCache[bucket.value].length > 0;

      return (
        <div key={bucket.value} className={`facet-item`} style={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Show caret only for source_name_ims (level 1); no caret for source_sublevel */}
            {currentLevel === 1 && (
              <span onClick={() => handleExpandLibrary(bucket.value, currentLevel, bucket.value)} style={{ cursor: 'pointer', marginRight: '8px' }}>
                {expanded.has(bucket.value) ? <i className="fa fa-caret-down" aria-hidden="true"></i> : <i className="fa fa-caret-right" aria-hidden="true"></i>}
              </span>
            )}
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={selectedLibrary[currentFacetKey]?.includes(bucket.value)}
                onChange={() => handleLibraryFacetClick(currentLevel, bucket.value)}
                style={{ marginRight: '8px' }}
              />
              {bucket.value} <span className="facet-count">({bucket.count})</span>
            </label>
          </div>

          {/* Only render source_sublevel when a source_name_ims item is expanded */}
          {expanded.has(bucket.value) && currentLevel === 1 && (
            <div style={{ marginLeft: '30px', marginTop: '4px', display: 'flex', flexDirection: 'column' }}>
              {hasSubLevels ? (
                sourceSubCache[bucket.value].map((subBucket) => renderFacetItem(subBucket, currentLevel + 1))
              ) : (
                <div style={{ marginLeft: '20px', color: 'gray' }}>No more sublevels available.</div>
              )}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="facet-container">
        {facetData.map((bucket) => renderFacetItem(bucket, level))}
      </div>
    );
  };

  // Render regulation facets: document_type (expandable) then document_subtype (no expand caret)
  const renderRegulation = (level, facets) => {
    const facetKey = `document_type`;

    if (!facets || !facets[facetKey] || facets[facetKey].length === 0) {
      return <div>No regulations available for this level.</div>;
    }

    if (level === 1 && !facetsCopyRef.current) {
      facetsCopyRef.current = structuredClone(facets);
    }
    facetsCopy = facetsCopyRef.current;

    let facetData;
    if (level === 1) {
      facetData = facetsCopy[facetKey][0].data;
    } else {
      facetData = facets[facetKey][0].data;
    }

    const renderFacetItem = (bucket, currentLevel) => {
      if (bucket.value === 'Not Available') return null;

      const currentFacetKey = currentLevel === 1 ? 'document_type' : 'document_subtype';
      const hasSubLevels = regulationSubCache[bucket.value] && regulationSubCache[bucket.value].length > 0;

      return (
        <div key={bucket.value} className={`facet-item`} style={{ display: 'flex', flexDirection: 'column', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Show caret only for document_type (level 1); no caret for document_subtype */}
            {currentLevel === 1 && (
              <span onClick={() => handleExpandRegulation(bucket.value, currentLevel, bucket.value)} style={{ cursor: 'pointer', marginRight: '8px' }}>
                {expanded.has(bucket.value) ? <i className="fa fa-caret-down" aria-hidden="true"></i> : <i className="fa fa-caret-right" aria-hidden="true"></i>}
              </span>
            )}
            <label style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={selectedRegulation[currentFacetKey]?.includes(bucket.value)}
                onChange={() => handleRegulationFacetClick(currentLevel, bucket.value)}
                style={{ marginRight: '8px' }}
              />
              {bucket.value} <span className="facet-count">({bucket.count})</span>
            </label>
          </div>

          {/* Only render subtypes when a document_type item is expanded */}
          {expanded.has(bucket.value) && currentLevel === 1 && (
            <div style={{ marginLeft: '30px', marginTop: '4px', display: 'flex', flexDirection: 'column' }}>
              {hasSubLevels ? (
                regulationSubCache[bucket.value].map((subBucket) => renderFacetItem(subBucket, currentLevel + 1))
              ) : (
                <div style={{ marginLeft: '20px', color: 'gray' }}>No more sublevels available.</div>
              )}
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="facet-container">
        {facetData.map((bucket) => renderFacetItem(bucket, level))}
      </div>
    );
  };

  useEffect(() => {
    if(localStorage.getItem('sliderSaveCriteria')){
      setTimeout(()=>{
        let newValue = localStorage.getItem('sliderSaveCriteria');
        let from;
        let showfilter;
        newValue = Number(newValue);
        if (newValue === 0) {
          showfilter = false;
          from = "1950-10-05T14:48:00.000Z";
        }
        else if (newValue === 20) {
          showfilter = true;
          from = new Date(new Date().setDate(new Date().getDate() - 7)).toISOString();
        }
        else if (newValue === 40) {
          showfilter = true;
          from = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString();
        } else if (newValue === 60) {
          showfilter = true;
          from = new Date(new Date().setDate(new Date().getDate() - 90)).toISOString();
        } else if (newValue === 80) {
          showfilter = true;
          from = new Date(new Date().setDate(new Date().getDate() - 180)).toISOString();
        } else if (newValue === 100) {
          showfilter = true;
          from = new Date(new Date().setDate(new Date().getDate() - 365)).toISOString();
        }

        getEffectiveDate(from,showfilter);
      },200);
    }

    if (window.location.href.includes('?q=')) {
      setTimeout(() => {
        let param = new URLSearchParams(window.location.search);
        let queryStringValue = param.get('q');
        const wrapper = document.getElementsByClassName("sui-search-box__wrapper")[0];
        if (wrapper) {
          const inputEl = wrapper.querySelector('input');
          if (inputEl) inputEl.value = queryStringValue || '';
        }
      }, 5000);
      setTimeout(() =>{
        if(window.location.search.includes('Procedure&filters')) {
          const el = document.getElementById('procedures');
          if (el) el.click();
        }
      },3000);
    }

    getSearch(effectiveDate,showFilter);
  }, [searchTerm, searchValue]);

  const getSearch = (effectiveDate,showfilter,showTreeView,treeViewFilter) => {
    let processedQuery = queryProcessor(
      searchValue === "*" ? " " : searchValue,
      connector,effectiveDate,showfilter,showTreeView,treeViewFilter
    );

    function checkIfValidUUID(str) {
      const regexExp = /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/gi;
      return regexExp.test(str);
    }

    const GetSavedSearchResult = async (searchValue) => {
      const response = await GetSavedSearchUUID(searchValue)
        ?.then((response) => {
          if (response.data.hits.hits[0]._source.query.lastPushSearchString) {
            if(!response.data.hits.hits[0]._source.finalNotificationAPIPayload){
              localStorage.setItem('sliderSaveCriteria', response.data.hits.hits[0]._source.dateValue);
            }
            else{
              localStorage.setItem('sliderSaveCriteria', response.data.hits.hits[0]._source.finalNotificationAPIPayload.dateValue);
            }
            window.location.href = (window.location.origin + "/?" + response.data.hits.hits[0]._source.query.lastPushSearchString);
          }
          else{
            processedQuery.searchQuery = response.data.hits.hits[0]._source.query.searchQuery;
            processedQuery.searchQuery.filters = response.data.hits.hits[0]._source.query.state.filters;

            setConfig(processedQuery);
            setisload(true);

            setTimeout(() => {
              const wrapper = document.getElementsByClassName("sui-search-box__wrapper")[0];
              if (wrapper) {
                const inputEl = wrapper.querySelector('input');
                if (inputEl) inputEl.value = processedQuery.searchQuery.query || '';
              }
            }, 3500);
          }
          return response;
        })
        .catch((error) => { console.log(error) });
    }

    if (checkIfValidUUID(searchValue)) {
      GetSavedSearchResult(searchValue);
    }
    else {
      setTimeout(() => {
        if (sessionStorage.MuleApiHrData){
          const MuleApiHrData = JSON.parse(sessionStorage.MuleApiHrData);
          if (MuleApiHrData.businessAddressCountry && MuleApiHrData.myServicesLanguage) {
            var CountryFullName = CountryWithShortName.filter(function (item) {
              return item.code.toLowerCase() == MuleApiHrData.businessAddressCountry.toLowerCase();
            });
            var LanguageFullName = LanguageWithShortName.filter(function (item) {
              return item.code.toLowerCase() == MuleApiHrData.myServicesLanguage.toLowerCase();
            });

            processedQuery.searchQuery.boosts = {
              "country": [
                {
                  "type": "value",
                  "value": CountryFullName[0]?.name,
                  "operation": "add",
                  "factor": 1000.0
                }
              ],
              "languages": [
                {
                  "type": "value",
                  "value": LanguageFullName[0]?.name,
                  "operation": "add",
                  "factor": 1000.0
                }
              ]
            };
          }
        }

        setConfig(processedQuery);
        setisload(true);

        setTimeout(() => {
          const wrapper = document.getElementsByClassName("sui-search-box__wrapper")[0];
          if (wrapper) {
            const inputEl = wrapper.querySelector('input');
            if (inputEl) inputEl.value = processedQuery.searchQuery.query || '';
          }
          document.getElementsByClassName('button sui-search-box__submit')[0]?.click();
        }, 100);

      }, 100);
    }

    // Regarding Translation Task -Start
    const getTranslatedTextSearchResult = (searchValue) => {
      var filters = { "all": [] };

      var search_fields = {
        "system_title": { "weight": 3.5 },
        "title":{ "weight":30.5 },
        "tags":{ "weight":1.5 },
        "division":{ "weight":1.5 },
        "country":{ "weight":1.5 },
        "description":{ "weight":1.0 },
        "body":{ "weight":1.2 },
        "approved": { "weight": 0.0 },
        "approver": { "weight": 0.0 },
        "author": { "weight": 0.0 },
        "comments": { "weight": 0.0 },
        "contact": { "weight": 0.0 },
        "created_by": { "weight": 0.0 },
        "doc_id": { "weight": 0.0 },
        "editor": { "weight": 0.0 },
        "enabling_and_support": { "weight": 0.0 },
        "extension": { "weight": 0.0 },
        "label": { "weight": 0.0 },
        "languages": { "weight": 0.0 },
        "org_level_1": { "weight": 0.0 },
        "org_level_2": { "weight": 0.0 },
        "org_level_3": { "weight": 0.0 },
        "org_level_4": { "weight": 0.0 },
        "org_level_5": { "weight": 0.0 },
        "owned_by": { "weight": 0.0 },
        "perm_id": { "weight": 0.0 },
        "procedures": { "weight": 0.0 },
        "process": { "weight": 0.0 },
        "regulation": { "weight": 0.0 },
        "site": { "weight": 0.0 },
        "source_name_ims": { "weight": 0.0 },
        "source_sublevel": { "weight": 0.0 }
      };

      var boosts = {
        "procedures": [
          { "type": "value", "value": "Procedures", "operation": "add", "factor": 100.0 }
        ],
        "source_name_ims": [
          { "type": "value", "value": "MARGO", "operation": "add", "factor": 200.0 }
        ],
        "source_sublevel": [
          { "type": "value", "value": "REGS-DE01 - CMS - Global Regulations", "operation": "add", "factor": 20.0 }
        ]
      };

      window.searchUI?.state?.filters?.forEach((c) => {
        var obj = {};
        obj[c.type] = [];
        c.values.forEach((b) => {
          var obj1 = {};
          obj1[c.field] = b;
          obj[c.type].push(obj1);
        });
        filters.all.push(obj);
      });

      let data = {
        "query": searchValue,
        "result_fields": { "title": { "raw": {} } },
        "page": { "current": 1, "size": 1 },
        filters,
        boosts,
        "precision": 8,
        search_fields
      };

      getSearchResults(data)
        ?.then((response) => {
          setsearchTranslatedValue(response.data.meta.page.total_results);
          setsearchTranslatedWord(searchValue);

          setTimeout(() => {
            document.getElementsByClassName('button sui-search-box__submit')[0]?.click();
          }, 1000);
        })
        .catch((error) => { console.log(error) });
    };

    const GetTranslatedResult = async (searchValue) => {
      const response = await GetTranslatedResult_Service(searchValue)
        ?.then((response) => {
          if(response.data.meta.detected_source_language[0] !== "en" && response.data.results[0] !== searchValue){
            getTranslatedTextSearchResult(response.data.results[0]);
          } else {
            setsearchTranslatedValue(0);
          }
          return response;
        })
        .catch((error) => { console.log(error) });
    };

    if (searchValue) {
      GetTranslatedResult(searchValue);
    } else { setsearchTranslatedWord(searchValue); }
    // Regarding Translation Task -End
  };

  const getEffectiveDate = (date,showfilter)=>{
    setshowFilter(showfilter);
    setEffectiveDate(date);
    setTimeout(()=>{
      getSearch(date,showfilter);
    },2000);
  };

  return (
    <Container fluid>
      <Row>
        <Col md="12">
          {isload ? (
            <SearchProvider config={config}>
              <WithSearch
                mapContextToProps={({ results, wasSearched, facets }) => ({
                  results,
                  wasSearched, facets
                })}
              >
                {({ results, wasSearched, facets }) => {
                  console.log('Results123:', results);
                  console.log('Was Searched:', wasSearched);
                  console.log('facets:', facets );

                  return (
                    <div>
                      <ErrorBoundary>
                        <Layout
                          header={
                            <>
                              <Searchbox
                                getSerchterm={setSearchValue}
                                translatedWord={searchTranslatedWord}
                                translatedResultCount={searchTranslatedValue}
                              />
                            </>
                          }
                          sideContent={
                            <Sidecontent
                              getEffectiveDate={getEffectiveDate}
                              hierarchicalFacets={facets ? renderFacets(1, facets) : <div>No facets available.</div>}
                              hierarchicalLibrary={facets ? renderLibrary(1, facets) : <div>No sources available.</div>}
                              hierarchicalRegulation={facets ? renderRegulation(1, facets) : <div>No regulations available.</div>}
                            />
                          }
                          bodyContent={
                            <Bodycontent
                              wasSearched={wasSearched}
                              searchValue={searchValue}
                            />
                          }
                          bodyFooter={<Paging />}
                        />
                        <Appfooter />
                      </ErrorBoundary>
                    </div>
                  );
                }}
              </WithSearch>
            </SearchProvider>
          ) : (
            <LoadingOverlay
              active={true}
              spinner
              text="Loading ..."
            ></LoadingOverlay>
          )}
        </Col>
      </Row>
    </Container>
  );
};